import { Controller } from "@hotwired/stimulus";
import { useClickOutside } from "stimulus-use";

// Select multiple simple (sans tags visuels)
// Basé sur le template Preline pour les sélections multiples simples
export default class extends Controller {
  static targets = [
    "hiddenSelect", // Le <select> original et caché (avec multiple)
    "wrapper", // Le div qui affiche la sélection et fait office de bouton
    "dropdown", // Le menu déroulant avec les options
    "option", // Chaque option cliquable dans le dropdown
    "checkIcon", // Les icônes de validation dans les options
    "placeholder", // L'élément qui affiche le placeholder
  ];

  static values = {
    placeholder: String,
    allowBlank: Boolean,
  };

  connect() {
    // Utilise stimulus-use pour fermer le dropdown si on clique à l'extérieur
    if (this.hasDropdownTarget) {
      useClickOutside(this, { element: this.dropdownTarget });
    }

    // Rendre la sélection initiale
    this.renderInitialSelection();

    // Surveiller les changements sur le select caché (utile pour des mises à jour programmatiques)
    if (this.hasHiddenSelectTarget) {
      this.hiddenSelectTarget.addEventListener(
        "change",
        this.renderInitialSelection.bind(this)
      );

      // Intercepter l'événement invalid pour rediriger le focus vers le wrapper visible
      this.hiddenSelectTarget.addEventListener(
        "invalid",
        this.handleInvalidValidation.bind(this)
      );
    }
  }

  // Action appelée quand on clique en dehors du composant
  clickOutside(event) {
    if (this.dropdownTarget.classList.contains("hidden")) return;
    this.hideDropdown();
  }

  // Affiche ou cache le dropdown
  toggleDropdown(event) {
    event.preventDefault();
    this.dropdownTarget.classList.toggle("hidden");
  }

  // Gère le clic sur le label associé
  handleLabelClick(event) {
    event.preventDefault();
    // Ouvrir le dropdown si fermé, sinon ne rien faire
    if (this.dropdownTarget.classList.contains("hidden")) {
      this.dropdownTarget.classList.remove("hidden");
    }
  }

  hideDropdown() {
    this.dropdownTarget.classList.add("hidden");
  }

  // Gère l'événement invalid sur le select caché pour rediriger le focus vers le wrapper visible
  handleInvalidValidation(event) {
    event.preventDefault(); // Empêche l'affichage du message d'erreur natif

    // Focuser sur le wrapper visible et ajouter une indication visuelle d'erreur
    if (this.hasWrapperTarget) {
      this.wrapperTarget.focus();
      this.wrapperTarget.classList.add("border-red-500", "ring-2", "ring-red-200");
      
      // Optionnel : afficher le dropdown pour inviter l'utilisateur à choisir
      if (this.dropdownTarget.classList.contains("hidden")) {
        this.dropdownTarget.classList.remove("hidden");
      }

      // Retirer l'indication d'erreur après un délai ou dès qu'une sélection est faite
      setTimeout(() => {
        this.wrapperTarget.classList.remove("border-red-500", "ring-2", "ring-red-200");
      }, 3000);
    }
  }

  // Gère la sélection/désélection d'une option depuis le dropdown
  selectOption(event) {
    const selectedOptionDiv = event.currentTarget;
    const { value } = selectedOptionDiv.dataset;

    // Trouver l'option dans le select caché
    const originalOption = this.findOriginalOption(value);
    if (!originalOption) return;

    // Basculer la sélection
    originalOption.selected = !originalOption.selected;

    // Mettre à jour l'affichage
    this.updatePlaceholderDisplay();
    this.updateOptionsState();

    // Retirer les classes d'erreur de validation si elles sont présentes
    if (this.hasWrapperTarget) {
      this.wrapperTarget.classList.remove("border-red-500", "ring-2", "ring-red-200");
    }

    // Déclencher l'événement change sur le select caché pour l'auto-submit
    if (this.hasHiddenSelectTarget) {
      this.hiddenSelectTarget.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Ne pas fermer le dropdown en mode multiple pour permettre plusieurs sélections
    // this.hideDropdown();
  }

  // Met à jour l'affichage du placeholder avec le nombre d'éléments sélectionnés
  updatePlaceholderDisplay() {
    if (!this.hasPlaceholderTarget) return;

    const selectedOptions = Array.from(this.hiddenSelectTarget.options).filter(option => option.selected);
    const selectedCount = selectedOptions.length;

    if (selectedCount === 0) {
      this.placeholderTarget.textContent = this.placeholderValue;
      this.placeholderTarget.classList.remove("font-semibold");
    } else if (selectedCount === 1) {
      this.placeholderTarget.textContent = selectedOptions[0].text;
      this.placeholderTarget.classList.add("font-semibold");
    } else {
      this.placeholderTarget.textContent = `${selectedCount} éléments sélectionnés`;
      this.placeholderTarget.classList.add("font-semibold");
    }
  }

  // Met à jour l'état visuel des options (checked/unchecked)
  updateOptionsState() {
    const selectedValues = Array.from(this.hiddenSelectTarget.options)
      .filter(option => option.selected)
      .map(option => option.value);
    
    this.optionTargets.forEach((optionEl) => {
      const optionValue = optionEl.dataset.value;
      const isSelected = selectedValues.includes(optionValue);
      
      // Trouver l'icône checkIcon dans cette option spécifique
      const checkIcon = this.checkIconTargets.find(icon => 
        optionEl.contains(icon)
      );
      const titleElement = optionEl.querySelector('[data-title]');
      
      if (isSelected) {
        // Afficher l'icône et mettre en gras le texte pour l'option sélectionnée
        if (checkIcon) {
          checkIcon.classList.remove("hidden");
        }
        if (titleElement) {
          titleElement.classList.add("font-semibold");
        }
      } else {
        // Masquer l'icône et enlever le gras pour les options non sélectionnées
        if (checkIcon) {
          checkIcon.classList.add("hidden");
        }
        if (titleElement) {
          titleElement.classList.remove("font-semibold");
        }
      }
    });
  }

  // ========== Méthodes privées de rendu et d'aide ==========

  // Au chargement, affiche la sélection initiale
  renderInitialSelection() {
    if (!this.hasHiddenSelectTarget) return;

    this.updatePlaceholderDisplay();
    this.updateOptionsState();
  }

  // Retrouve une <option> originale dans le <select> caché par sa valeur
  findOriginalOption(value) {
    if (!this.hasHiddenSelectTarget) return null;
    return Array.from(this.hiddenSelectTarget.options).find(
      (o) => o.value === value
    );
  }

  // Retrouve un <div> d'option dans le dropdown par sa valeur
  findDropdownOption(value) {
    return this.optionTargets.find((el) => el.dataset.value === value);
  }
}
